import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import type { ConversationFilter } from '@/lib/api';
import { FilterSheet } from '@/components/FilterSheet';

interface ChipDef {
  value: ConversationFilter;
  label: string;
}

// 2026-06-02 (bundle A) — ten client-side filters. Order locked by Cowork
// hand-off: All, Needs you, Hot, Drip Active, Awaiting Reply, Quoted / No
// Booking, Owner-Handled, Stale, Booked, Archived. Exported so FilterSheet
// renders the same list + labels (single source of truth).
export const CHIP_DEFS: ChipDef[] = [
  { value: 'all', label: 'All' },
  { value: 'needs', label: 'Needs you' },
  { value: 'hot', label: 'Hot' },
  // 2026-07-02 (DemoPolish 4) — the one Leads pipeline stage that had no
  // Chats filter (server-side filter=quoted). With it, all four Leads
  // stages exist here: Needs you / Quoted / Booked / Archived.
  { value: 'quoted', label: 'Quoted' },
  { value: 'drip_active', label: 'Drip Active' },
  { value: 'awaiting_reply', label: 'Awaiting Reply' },
  { value: 'quoted_no_booking', label: 'Quoted / No Booking' },
  { value: 'owner_handled', label: 'Owner-Handled' },
  { value: 'stale', label: 'Stale' },
  { value: 'booked', label: 'Booked' },
  { value: 'resolved', label: 'Archived' },
];

// 2026-06-15 — hybrid: these three stay inline as pills; the rest live in the
// Filters sheet.
const INLINE_KEYS: ConversationFilter[] = ['all', 'needs', 'hot'];

// Concise labels shown ON the Filters button when it reflects a long non-inline
// filter, so the strip never overflows the row. Full names stay in the sheet.
// Anything not listed uses its full label (short enough); `truncate` is the
// hard backstop either way.
const BUTTON_LABELS: Partial<Record<ConversationFilter, string>> = {
  quoted_no_booking: 'Quoted',
  awaiting_reply: 'Awaiting',
  owner_handled: 'Owner',
};

interface FilterChipsProps {
  value: ConversationFilter;
  onChange: (next: ConversationFilter) => void;
  /**
   * Optional per-filter unread/total counts. When provided, a count badge
   * is rendered inside the chip. Counts of 0 are hidden (except "All",
   * which always shows). R11 consult 2026-05-06.
   */
  counts?: Partial<Record<ConversationFilter, number>>;
}

export function formatCount(n: number): string {
  if (n > 99) return '99+';
  return String(n);
}

// Tinted pill style (replaces the old bordered boxes): active = soft blue
// fill + blue text, no border; inactive = transparent + secondary text.
const PILL_ACTIVE =
  'bg-[#E6F1FB] text-[#0C447C] dark:bg-[#15315A] dark:text-[#9CC4F0]';
const PILL_INACTIVE =
  'text-ink-500 hover:bg-ink-100/60 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]';

export function FilterChips({ value, onChange, counts }: FilterChipsProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  // When the active filter is NOT one of the inline pills, the Filters button
  // takes the active style + shows the applied filter's label/count so the
  // owner always sees what's applied without opening the sheet.
  const activeIsInline = INLINE_KEYS.includes(value);
  const sheetFilter = activeIsInline ? undefined : CHIP_DEFS.find((c) => c.value === value);
  const sheetCount = sheetFilter ? counts?.[sheetFilter.value] : undefined;
  // Concise label for the button reflection so a long filter name doesn't run
  // off the row. Full names stay in the sheet.
  const buttonLabel = sheetFilter ? (BUTTON_LABELS[sheetFilter.value] ?? sheetFilter.label) : 'Filters';

  return (
    <div className="flex items-center gap-1.5 overflow-hidden bg-ink-50 pl-3 pr-3 pt-1.5 pb-3 dark:bg-[#0B1220]">
      <div role="tablist" aria-label="Filter conversations" className="flex shrink-0 items-center gap-1">
        {CHIP_DEFS.filter((c) => INLINE_KEYS.includes(c.value)).map((c) => {
          const active = c.value === value;
          const count = counts?.[c.value];
          const showCount = c.value === 'all' ? typeof count === 'number' : Boolean(count);
          return (
            <button
              key={c.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(c.value)}
              className={[
                'flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors',
                'min-h-[32px]',
                active ? PILL_ACTIVE : PILL_INACTIVE,
              ].join(' ')}
            >
              <span>{c.label}</span>
              {showCount ? (
                <span
                  className={[
                    'text-[11px] font-semibold tabular-nums',
                    active ? 'opacity-80' : 'opacity-60',
                  ].join(' ')}
                >
                  {formatCount(count ?? 0)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Filters button — reflects a non-inline active filter, else neutral. */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
        className={[
          'ml-auto flex min-w-0 items-center gap-1.5 rounded-full border-[0.5px] px-3 py-1.5 text-[13px] font-medium transition-colors',
          'min-h-[32px]',
          sheetFilter
            ? `border-transparent ${PILL_ACTIVE}`
            : 'border-ink-300 bg-white text-ink-700 hover:bg-ink-100/60 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]',
        ].join(' ')}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{buttonLabel}</span>
        {sheetFilter && typeof sheetCount === 'number' && sheetCount > 0 ? (
          <span className="shrink-0 text-[11px] font-semibold tabular-nums opacity-80">
            {formatCount(sheetCount)}
          </span>
        ) : null}
      </button>

      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        value={value}
        onChange={onChange}
        counts={counts}
      />
    </div>
  );
}
